import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../shared/services/auth/auth.service';
import { AuthResponseDTO } from '../dto/responses/AuthResponseDTO';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserService } from '../shared/services/user/user.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private router: Router,
    private authService: AuthService,
    private userService: UserService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Excluir los endpoints de login y refresh token para evitar bucles infinitos
    if (req.url.includes('/login') || req.url.includes('/refresh-token')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Si el error es un 401, redirigir al login
        if (error.status === 401) {
          return this.tryRefreshToken(req, next);
        }
        return throwError(() => new Error(error.message));
      })
    );
  }

  private tryRefreshToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.authService.getRefreshToken()).pipe(
      switchMap((rToken) => {
        if (rToken) {
          return this.authService.refreshToken(rToken).pipe(
            switchMap(({ accessToken, refreshToken }: AuthResponseDTO) => {
              this.authService.setToken(accessToken);
              if (refreshToken) {
                this.authService.setRefreshToken(refreshToken);
                this.userService.getUser();
              }

              return this.repeatRequestWithNewToken(req, next, accessToken);
            }),
            catchError((refreshError) => this.handleRefreshTokenFailure(refreshError))
          );
        } else {
          return this.handleRefreshTokenFailure(new Error('No se encontró el refresh token'))
        }
      })
    );
  }

  private repeatRequestWithNewToken(req: HttpRequest<any>, next: HttpHandler, newToken: string): Observable<HttpEvent<any>> {
    const clonedRequest = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${newToken}`)
    });
    return next.handle(clonedRequest);
  }

  private handleRefreshTokenFailure(error: any): Observable<never> {
    this.authService.removeTokens();
    this.router.navigate(['/login'], { replaceUrl: true });
    return throwError(() => new Error(error.message || 'No se pudo obtener un nuevo token de acceso'));
  }
}
